import {
  watch,
  nextTick,
  type Ref,
  type WatchSource,
  type ComponentPublicInstance,
} from 'vue'

// Type for elements that can be focused
type FocusableElement = HTMLElement | ComponentPublicInstance

// Type guard to check if an object has a $el property (Vue component instance)
function hasEl(obj: unknown): obj is { $el: HTMLElement } {
  return typeof obj === 'object' && obj !== null && '$el' in obj
}

interface UseOverlayFocusOptions {
  /**
   * Whether to automatically set tabindex="-1" on the overlay element.
   * Makes the element programmatically focusable.
   * @default true
   */
  setTabIndex?: boolean
}

/**
 * Manages focus for overlay components (modals, lightboxes and etc.).
 * Automatically focuses the overlay element when it opens and optionally sets tabindex for better accessibility.
 */
export function useOverlayFocus<T extends FocusableElement = HTMLElement>(
  overlayRef: Ref<T | null>,
  isOpen: WatchSource<boolean>,
  options: UseOverlayFocusOptions = {},
) {
  const { setTabIndex = true } = options
  watch(
    [isOpen, overlayRef],
    async ([newIsOpen, newRef]) => {
      if (newRef) {
        await nextTick()
        // Handle both Vue component instances (with $el) and raw HTML elements
        const element = hasEl(newRef) ? newRef.$el : (newRef as HTMLElement)

        if (element) {
          // Set tabindex to make element programmatically focusable
          if (setTabIndex && !element.hasAttribute('tabindex')) {
            element.setAttribute('tabindex', '-1')
          }

          // Focus the element if overlay is open
          if (newIsOpen && typeof element.focus === 'function') {
            element.focus()
          }
        }
      }
    },
    { immediate: true }, // Check immediately if already open
  )
}
